import type { PlayerInput } from "@arcade-hockey/shared";
import { type MatchState, type PlayerState, type TacticId, tuning } from "@arcade-hockey/sim";
import { fillChase } from "../geometry/chase.js";
import { homePosition } from "../positioning/home-position.js";
import { classifySituation } from "../situation.js";

// Module-scoped scratch vec, reused every tick by every anchor caller. The
// bot loop calls fillAnchorInput at most once per skater per tick, so the
// single shared scratch is safe — its lifetime is "during this call".
const ANCHOR_SCRATCH = { x: 0, y: 0 };

function tacticFor(state: MatchState, teamId: PlayerState["teamId"]): TacticId {
  for (const team of state.control) {
    if (team.teamId === teamId) return team.tactic;
  }
  return "balanced";
}

// Steer toward this skater's role-and-situation-specific home position.
// Phase G slice 2: invoked from every dispatch branch where a bot is NOT
// actively handling the puck:
//   - chaser branch, when not closest to a loose puck
//   - support branch, when our team owns the puck (we are not the carrier)
//   - defender branch, when an opponent owns the puck (we are not the
//     pressurer)
// The result: every non-engaged bot positions itself based on (role,
// situation) instead of a single uniform formula, so a 5-on-5 looks
// visibly different across forecheck, breakout, neutral-zone transition,
// and defensive-zone defense.
//
// Phase G slice 6 adds the team's `tactic` selection: balanced / trap /
// aggressive scale the anchor by a per-tactic modifier so the same
// (role × situation) cell shapes the team differently depending on the
// strategic preset.
export function fillAnchorInput(input: PlayerInput, player: PlayerState, state: MatchState): void {
  const situation = classifySituation(state, player.teamId);
  const tactic = tacticFor(state, player.teamId);
  homePosition(
    ANCHOR_SCRATCH,
    player.position,
    player.teamId,
    tactic,
    situation,
    state.puck.pos.x,
    state.puck.pos.y,
  );
  fillChase(input, player, ANCHOR_SCRATCH.x, ANCHOR_SCRATCH.y, tuning.positioning.anchorDeadband);
}
